'use client'

import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useCoolClubPrice } from '@/hooks/use-coolclub-price'
import { cn, formatPrice } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'
import { useProductCardContext } from './context/product-card-context'
import { ProductItemVariant } from './product-item-variant'
import { ProductItemVariantMobile } from './product-item-variant-mobile'

export const ProductItemInfo = () => {
  const { product, trackingClickProduct } = useProductCardContext()
  const isMobile = useIsMobileClient()

  // Sử dụng hook tùy chỉnh để xử lý giá CoolClub
  const { productData, isCoolClubSale } = useCoolClubPrice(product)

  const handleClick = () => {
    trackingClickProduct()
  }

  return (
    <figcaption className='flex flex-col gap-1 lg:gap-2'>
      {isMobile ? <ProductItemVariantMobile /> : <ProductItemVariant />}

      <Link
        href={productData?.href || '/'}
        className='w-fit'
        aria-label={`Xem chi tiết sản phẩm ${productData?.title}`}
        data-tracking-level-99={`Product-item-${productData?.id}`}
        onClick={handleClick}
        prefetch={false}
      >
        <h3 className='line-clamp-2 font-sans text-xs leading-3.5 text-neutral-800 hover:text-primary lg:text-base lg:leading-5'>
          {productData?.title}
        </h3>
      </Link>

      <div className='flex flex-wrap items-center gap-1'>
        {isCoolClubSale && (
          <Image
            src='/icons/icon-price-signin.png'
            alt='Biểu tượng giá ưu đãi khi đăng nhập'
            width={16}
            height={16}
            loading='lazy'
            decoding='async'
          />
        )}
        <p
          className={cn(
            'font-sans text-xs font-bold leading-3.5 text-neutral-900 lg:text-base lg:leading-5',
            isCoolClubSale && 'text-primary'
          )}
        >
          {formatPrice(productData?.regular_price ?? 0)}
        </p>

        {productData?.sale_percent ? (
          <TagThumbnail
            title={`-${productData.sale_percent}%`}
            variant='primary'
            size='small'
            className='px-1.5 text-xxs font-semibold leading-3 text-light lg:text-sm lg:leading-4.5'
          />
        ) : null}

        {productData?.compare_price &&
        productData.compare_price !== productData.regular_price ? (
          <p className='text-xs leading-3.5 text-neutral-400 line-through lg:text-sm lg:leading-4.5'>
            {formatPrice(productData?.compare_price ?? 0)}
          </p>
        ) : null}
      </div>

      {productData?.coolclub_sale?.active && !isCoolClubSale && (
        <p className='line-clamp-1 text-xs text-neutral-500'>
          Giá coolclub: {formatPrice(productData.coolclub_sale.coolclub_price)}
        </p>
      )}

      {productData?.note && (
        <p className='line-clamp-2 font-sans text-xs italic leading-3.5 text-primary-500 lg:text-sm lg:leading-4.5'>
          {productData.note}
        </p>
      )}

      <span className='sr-only'>
        {`Sản phẩm ${productData?.title} có giá ${formatPrice(productData?.regular_price ?? 0)}${
          productData?.sale_percent
            ? ` (Giảm ${productData.sale_percent}% từ ${formatPrice(productData?.compare_price ?? 0)})`
            : ''
        }${productData?.note ? `. ${productData.note}` : ''}`}
      </span>
    </figcaption>
  )
}
